import AwsS3 from '@uppy/aws-s3'
import type { AwsS3Options } from '@uppy/aws-s3'
import { Uppy } from '@uppy/core'
import type { Body, Meta } from '@uppy/core'
import Tus from '@uppy/tus'

import type {
  S3PreSignedURLInput,
  UserS3UploadPreSignedURLResponse,
} from './getS3PresignUrl'
import { isValidUploadType, UploadType } from './getS3PresignUrl'
import logger from './logger'
import { createTusOptions } from './uppy/createTusOptions'
import type { UploaderBody, UploaderMeta } from './uppy/UppyFile'
import type { UploadTransport } from './values/UploadTransport'

export type UploaderUppy = Uppy<UploaderMeta, UploaderBody>

const uppyLogger = {
  debug: logger.extend('uppy').extend('debug'),
  warn: logger.extend('uppy').extend('warn'),
  error: logger.extend('uppy').extend('error'),
}

export function createUppy({
  id,
  organizationId,
  getS3PresignUrl,
  transport = { type: 'awsS3' },
}: {
  id: string
  organizationId: string
  userId: string
  getS3PresignUrl: ({
    organizationId,
    fileName,
    mimeType,
    uploadType,
  }: S3PreSignedURLInput) => Promise<UserS3UploadPreSignedURLResponse>
  transport?: UploadTransport
}): UploaderUppy {
  const uppyInstance = new Uppy<UploaderMeta, UploaderBody>({
    id,
    autoProceed: true,
    logger: uppyLogger,
  })

  // Instance-wide meta and locale go through setOptions: the constructor demands
  // a complete metadata object and a locale carrying its own pluralize rule.
  uppyInstance.setOptions({
    meta: { uploaderId: id },
    locale: {
      strings: {
        noDuplicates: "Cannot add '%{fileName}', it already exists",
      },
    },
  })

  // The upload service pre-signs nothing and finalizes the upload itself: with
  // no aws-s3 plugin installed, getS3PresignUrl is never reached on this path.
  if (transport.type === 'tus') {
    uppyInstance.use(Tus, createTusOptions(transport))

    return uppyInstance
  }

  const aws3Config: AwsS3Options<Meta, Body> = {
    // Presigned single-request PUTs; the multipart flow would need the create,
    // part and complete requests the presign API does not answer.
    shouldUseMultipart: false,
    limit: 10,
    generateObjectKey: (file) => file.id,
    async signRequest(request) {
      const file = uppyInstance.getFile(request.key)

      if (!file) {
        throw new Error('Cannot presign an upload for a removed file')
      }

      const s3UploadSignatureUrl = await getS3PresignUrl({
        organizationId,
        fileName: file.name,
        mimeType: file.type || '',
        // the `id` for `createUppy` and the `Uploader` are of type `string`
        // because for the ideas composer, we pass the `id` as a string with the value `ideas`
        // For other instances of `Uploader`, the `id` is a value of UpdateType (post, channel avatar, account avatar)
        // So, we need to check if the `id` is a valid `UploadType` and if it is, we use it, otherwise we use `UploadType.postAsset`
        // Ideally, we should pass the UploadType when instantiating the `Uploader` but that would require a lot of changes
        // This PR already improve things and create a clean contract with the `getS3PresignUrl` function
        uploadType: isValidUploadType(id)
          ? UploadType[id]
          : UploadType.postAsset,
      })

      const { key, url } = s3UploadSignatureUrl

      // The S3 key only exists here; nothing downstream can derive it from the
      // presigned URL, so persist it on the file for UppyFile.toS3Upload.
      uppyInstance.setFileState(file.id, { meta: { ...file.meta, key } })

      return { url, key }
    },
  }

  uppyInstance.use(AwsS3, aws3Config)

  return uppyInstance
}
